import Vue from 'vue';
import httpClient from '@/httpClient/httpClient';
import {
  AuthRule,
  ResponseBody,
  RootState,
  UserInterface,
  UserDataInterface,
  userRoles,
  UserToken,
  DataProvider,
  ResponseDataProvider,
  DropdownList,
} from '@/types';
import { ActionTree, GetterTree, MutationTree } from 'vuex';
import configApi from '@/config';
import * as qs from 'qs';
import ResponseDataValue, {
  ResponseCodeDataValue,
  ResponseDataProviderValue,
} from '@/utils/ResponseDataValue';
import { Notification } from 'uiv';
import i18n from '@/i18n';

const REQUEST_ERROR = i18n.t('notifications.errorRequest');

const state: UserInterface = {
  id: null,
  name: null,
  surname: null,
  username: null,
  phone: null,
  email: null,
  isConfirmedEmail: null,
  timezone: null,
  language: null,
  currency: null,
  roles: [],
  authRule: [
    {
      role: userRoles.ADMIN,
      child: [userRoles.MANAGER, userRoles.SSP, userRoles.PUBLISHER],
    },
    {
      role: userRoles.MANAGER,
      child: [],
    },
    {
      role: userRoles.SSP,
      child: [],
    },
    {
      role: userRoles.PUBLISHER,
      child: [],
    },
  ],
  registerFormIsValid: false,
  registerFormStep: 1,
  cacheRegisterForm: {},
  timezones: {
    paginationData: null,
    data: [] as Array<DropdownList>,
  },
  users: {
    paginationData: null,
    data: [],
  },
};
const mutations: MutationTree<UserInterface> = {
  setToken(state, userToken: UserToken): void {
    // @ts-ignore
    Vue.localStorage.set(
      configApi.NAME_TOKEN_LOCAL_STORAGE,
      window.btoa(JSON.stringify(userToken))
    );
  },
  clearToken() {
    // @ts-ignore
    Vue.localStorage.remove(configApi.NAME_TOKEN_LOCAL_STORAGE);
  },
  logOut(state) {
    state.id = null;
    state.email = null;
    state.username = null;
    state.roles = [];
    // @ts-ignore
    Vue.localStorage.remove(configApi.NAME_TOKEN_LOCAL_STORAGE);
    // @ts-ignore
    Vue.localStorage.remove(configApi.ROLES_LOCAL_STORAGE);
  },
  setUser(state, data: UserDataInterface): void {
    state.id = data.id;
    state.name = data.name;
    state.surname = data.surname;
    state.phone = data.phone;
    state.email = data.email;
    state.timezone = data.timezone;
    state.username = data.username;
    state.language = data.language;
    state.currency = data.currency;
    state.roles = data.roles;
    state.isConfirmedEmail = data.is_confirmed_email;

    // @ts-ignore
    Vue.localStorage.set(
      configApi.ROLES_LOCAL_STORAGE,
      window.btoa(JSON.stringify(data.roles))
    );
  },
  setRoles(state, roles): void {
    state.roles = roles;
    // @ts-ignore
    Vue.localStorage.set(
      configApi.ROLES_LOCAL_STORAGE,
      window.btoa(JSON.stringify(roles))
    );
  },
  setFormStep(state, stepValue: number) {
    state.registerFormStep = stepValue;
  },
  setFormIsValid(state, value: boolean) {
    state.registerFormIsValid = value;
  },
  setCacheRegisterForm(state, formData) {
    state.cacheRegisterForm = { ...formData };
  },
  clearCacheRegisterForm(state) {
    state.cacheRegisterForm = {};
  },
  setProfileData(state, data) {
    state[data.name] = data.value;
  },
  setTimezones(state, data) {
    // if (!data.paginationData || !data.paginationData?.currentPage) {
    //   return;
    // }
    state.timezones.paginationData = data.paginationData;
    state.timezones.data = data.data;
  },
  setUsers(state, data: DataProvider): void {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.users.paginationData = data.paginationData;
    state.users.data = data.data;
  },
};

const getters: GetterTree<UserInterface, RootState> = {
  userCan: (state, getters) => (permissionName: string): boolean => {
    let result = false;
    let roles = state.roles;
    if (!state.roles.length) {
      roles = getters.getRoles;
    }
    if (!roles) {
      return false;
    }
    roles.find((role: string) => {
      if (permissionName == role) {
        result = true;
        return true;
      }
      state.authRule.find((auth: AuthRule) => {
        if (role !== auth.role) {
          return false;
        }
        if (auth.child.includes(permissionName)) {
          result = true;
          return true;
        }
        return false;
      });
    });
    return result;
  },
  getUser: (state): UserInterface => {
    return state;
  },
  getToken(): UserToken {
    // @ts-ignore
    const token = Vue.localStorage.get(configApi.NAME_TOKEN_LOCAL_STORAGE);
    return JSON.parse(token ? window.atob(token) : token);
  },
  getRoles(): AuthRule {
    // @ts-ignore
    const roles = Vue.localStorage.get(configApi.ROLES_LOCAL_STORAGE);
    return JSON.parse(roles ? window.atob(roles) : roles);
  },
  hasToken(): boolean {
    // @ts-ignore
    return !!Vue.localStorage.get(
      configApi.NAME_TOKEN_LOCAL_STORAGE,
      null,
      false
    );
  },
  getCacheRegisterForm(state) {
    return state.cacheRegisterForm;
  },
  getInitials() {
    let initials = '';

    if (state.name && state.surname) {
      initials = state.name[0] + state.surname[0];
    } else if (state.username) {
      initials = state.username[0];
    }

    return initials.toUpperCase();
  },
  getTimezoneList() {
    return state.timezones.data.map(
      (timezone): DropdownList => {
        return {
          label: timezone.label,
          value: timezone.id,
        };
      }
    );
  },
  userList(state) {
    return state.users.data.map((user) => {
      return {
        label: user.username,
        value: user.id,
      };
    });
  },
};

const actions: ActionTree<UserInterface, RootState> = {
  async login({ commit, dispatch }, formData) {
    try {
      const response: ResponseBody = await httpClient().post(
        '/auth/login',
        qs.stringify(formData)
      );
      commit('setToken', response.data.token);
      commit('setRoles', response.data.roles);
      return ResponseDataValue(response.data, true);
    } catch (e) {
      if (!e.response) {
        Notification.notify.error({
          title: REQUEST_ERROR,
        });
        throw REQUEST_ERROR;
      } else {
        return ResponseDataValue(e.response.data, false);
      }
    }
  },
  async getProfile({ commit }, data = {}) {
    try {
      const response: ResponseBody = await httpClient().get('/auth/profile', {
        params: Object.assign(
          {},
          {
            expand: 'name,surname',
            fields: 'id,username,email,roles,is_confirmed_email,status',
          },
          data
        ),
      });
      commit('setUser', response.data);
      return ResponseDataValue(response.data, true);
    } catch (e) {
      if (e.response.status === 401) {
        commit('logOut');
        window.location.href = '/';
      } else {
        Notification.notify.error({
          title: REQUEST_ERROR,
        });
      }
    }
  },
  async recovery({ commit }, formData) {
    try {
      const response: ResponseBody = await httpClient().post(
        '/auth/recovery',
        qs.stringify(formData)
      );
      return ResponseCodeDataValue(response.data, true, response.status);
    } catch (e) {
      if (!e.response) {
        Notification.notify.error({
          title: REQUEST_ERROR,
        });
        throw REQUEST_ERROR;
      } else {
        return ResponseCodeDataValue(e.response.data, false, e.response.status);
      }
    }
  },
  async resetPassword({ commit }, formData) {
    try {
      const response: ResponseBody = await httpClient().post(
        '/auth/reset-password',
        qs.stringify(formData)
      );
      return ResponseCodeDataValue(response.data, true, response.status);
    } catch (e) {
      if (!e.response) {
        Notification.notify.error({
          title: REQUEST_ERROR,
        });
        throw REQUEST_ERROR;
      } else {
        return ResponseCodeDataValue(e.response.data, false, e.response.status);
      }
    }
  },
  async validation({ commit }, formData) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/auth/validation',
        {
          params: Object.assign({}, formData),
        }
      );
      return ResponseCodeDataValue(response.data, true, response.status);
    } catch (e) {
      if (!e.response) {
        Notification.notify.error({
          title: REQUEST_ERROR,
        });
        throw REQUEST_ERROR;
      } else {
        return ResponseCodeDataValue(e.response.data, false, e.response.status);
      }
    }
  },
  async registrationPublisher({ commit }, formData) {
    try {
      const response: ResponseBody = await httpClient().post(
        '/auth/registration',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      commit('setToken', response.data.token);
      commit('setRoles', response.data.roles);
      return ResponseCodeDataValue(response.data, true, response.status);
    } catch (e) {
      if (!e.response) {
        Notification.notify.error({
          title: REQUEST_ERROR,
        });
        throw REQUEST_ERROR;
      } else {
        return ResponseCodeDataValue(e.response.data, false, e.response.status);
      }
    }
  },
  async registrationAdvertiser({ commit }, formData) {
    try {
      const response: ResponseBody = await httpClient().post(
        '/auth/registration',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return ResponseCodeDataValue(response.data, true, response.status);
    } catch (e) {
      if (!e.response) {
        Notification.notify.error({
          title: REQUEST_ERROR,
        });
        throw REQUEST_ERROR;
      } else {
        return ResponseCodeDataValue(e.response.data, false, e.response.status);
      }
    }
  },
  async resendConfirmEmail({ commit }) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/auth/resend-confirm-email'
      );
      return ResponseCodeDataValue(response.data, true, response.status);
    } catch (e) {
      if (!e.response) {
        Notification.notify.error({
          title: REQUEST_ERROR,
        });
        throw REQUEST_ERROR;
      } else {
        return ResponseCodeDataValue(e.response.data, false, e.response.status);
      }
    }
  },
  async sendConfirmedRegistrationToken({ commit }, formData) {
    try {
      const response: ResponseBody = await httpClient().put(
        '/auth/confirm-email',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return ResponseCodeDataValue(response.data, true, response.status);
    } catch (e) {
      if (!e.response) {
        Notification.notify.error({
          title: REQUEST_ERROR,
        });
        throw REQUEST_ERROR;
      } else {
        return ResponseCodeDataValue(e.response.data, false, e.response.status);
      }
    }
  },
  async updateProfile({ commit }, formData) {
    try {
      const response: ResponseBody = await httpClient().put(
        '/auth/profile',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return ResponseCodeDataValue(response.data, true, response.status);
    } catch (e) {
      if (!e.response) {
        Notification.notify.error({
          title: REQUEST_ERROR,
        });
        throw REQUEST_ERROR;
      } else {
        return ResponseCodeDataValue(e.response.data, false, e.response.status);
      }
    }
  },
  async getTimezones({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/dictionary/timezone',
        {
          params: Object.assign({}, data),
        }
      );
      const responseDataProvider: ResponseDataProvider = ResponseDataProviderValue(
        response,
        true
      );
      commit('setTimezones', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async users({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get('/user', {
        params: Object.assign({}, data, {
          role: 'manager',
          fields: 'id,username'
        })
      });
      const responseDataProvider: ResponseDataProvider = ResponseDataProviderValue(
        response,
        true
      );

      commit('setUsers', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async changePassword({ commit }, formData) {
    try {
      const response: ResponseBody = await httpClient().put(
        '/auth/change-password',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return ResponseCodeDataValue(response.data, true, response.status);
    } catch (e) {
      if (!e.response) {
        Notification.notify.error({
          title: REQUEST_ERROR,
        });
        throw REQUEST_ERROR;
      } else {
        return ResponseCodeDataValue(e.response.data, false, e.response.status);
      }
    }
  },
};

export default {
  state,
  mutations,
  getters,
  actions,
};
